import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { RulebookPage } from "@/components/RulebookPage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function Combat() {
  return (
    <RulebookPage title="Combat">
      <h2 className="text-xl font-semibold mt-6 mb-2">Weapons &amp; Armour</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Combat in Ultimate Warlock is simulated through the use of LARP-safe
        weapons. If you are struck on a limb or torso by a weapon, your
        character loses hit points.
      </p>

      <ScrollArea className="mb-4 w-full max-w-[90vw]">
        <table className="w-max max-w-[200vw] text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Weapon Size
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Damage
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Examples
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 border-t border-border">
                Small (&lt; 60cm)
              </td>
              <td className="px-3 py-2 border-t border-border">1</td>
              <td className="px-3 py-2 border-t border-border">
                Daggers, Handaxes, Shortswords
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">
                Medium (60cm+)
              </td>
              <td className="px-3 py-2 border-t border-border">2</td>
              <td className="px-3 py-2 border-t border-border">
                Swords, Axes, Maces
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">
                Large (60cm+, requires 2 hands)
              </td>
              <td className="px-3 py-2 border-t border-border">3</td>
              <td className="px-3 py-2 border-t border-border">Polearms</td>
            </tr>
          </tbody>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <p className="text-sm text-muted-foreground mb-3">
        Any damaging hit taken by a shield is completely negated. Shields do not
        protect you against spells that do not deal damage, such as Fear or
        Restrict.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        You only take the damage from a weapon once every 2 seconds — so
        rapid-fire &apos;machine gunning&apos; strikes only count as a single
        hit, even if you are dual wielding.
      </p>

      <ScrollArea className="mb-4 w-full max-w-[90vw]">
        <table className="w-max max-w-[200vw] text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Armour
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Wealth Level
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">AP</th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Damage Reduction
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Examples
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 border-t border-border">Scrap Armour</td>
              <td className="px-3 py-2 border-t border-border">1 (Peasant)</td>
              <td className="px-3 py-2 border-t border-border">+1</td>
              <td className="px-3 py-2 border-t border-border">—</td>
              <td className="px-3 py-2 border-t border-border">
                Layered rags, random junk
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">Light Armour</td>
              <td className="px-3 py-2 border-t border-border">
                2 (Merchant/Artisan)
              </td>
              <td className="px-3 py-2 border-t border-border">+1</td>
              <td className="px-3 py-2 border-t border-border">-1</td>
              <td className="px-3 py-2 border-t border-border">
                Leather, furs and animal hides
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">
                Medium Armour
              </td>
              <td className="px-3 py-2 border-t border-border">3 (Wealthy)</td>
              <td className="px-3 py-2 border-t border-border">+3</td>
              <td className="px-3 py-2 border-t border-border">-1</td>
              <td className="px-3 py-2 border-t border-border">
                Metal breastplate, chainmail
              </td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">Heavy Armour</td>
              <td className="px-3 py-2 border-t border-border">4 (Noble)</td>
              <td className="px-3 py-2 border-t border-border">+3</td>
              <td className="px-3 py-2 border-t border-border">-2</td>
              <td className="px-3 py-2 border-t border-border">
                As Medium Armour plus a helmet
              </td>
            </tr>
          </tbody>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <p className="text-sm text-muted-foreground mb-3">
        Your Wealth Level dictates what kind of armour your character has access
        to. The damage reduction of armour can reduce damage to 0, making you
        immune to small blows from weak enemies. However, this only applies to
        physical blows — it does not protect against magical attacks such as the
        Blast spell. Damage reduction only applies if you are hit in an armoured
        spot.
      </p>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Calling Your Hits</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <div>
            <p>
              Your opponents in a fight might not know exactly how much damage
              you&apos;re doing, or (if they&apos;re wearing armour) if
              they&apos;ve even been hit at all!
            </p>
            <p>
              Help out your fellow players by verbally calling your hits when
              you connect.
            </p>
          </div>
        </CardContent>
      </Card>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Firearms</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Toy guns that fire soft foam pellets are used to represent Firearms in
        Ultimate Warlock.
      </p>

      <ScrollArea className="mb-4 w-full max-w-[90vw]">
        <table className="w-max max-w-[200vw] text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Firearm
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Damage
              </th>
              <th className="text-left px-3 py-2 bg-muted font-semibold">
                Examples
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2 border-t border-border">One-handed</td>
              <td className="px-3 py-2 border-t border-border">2</td>
              <td className="px-3 py-2 border-t border-border">Pistols</td>
            </tr>
            <tr>
              <td className="px-3 py-2 border-t border-border">Two-handed</td>
              <td className="px-3 py-2 border-t border-border">3</td>
              <td className="px-3 py-2 border-t border-border">
                Muskets, Rifles
              </td>
            </tr>
          </tbody>
        </table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <p className="text-sm text-muted-foreground mb-3">
        Firearms in Ultimate Warlock are not advanced enough (yet) to fire
        automatically — and no Firearm can hold a magazine of more than 6 shots
        before needing to be reloaded. It is recommended to verbally call your
        hits in combat, as foam pellets are often too soft to be felt by
        armoured targets.
      </p>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Explosives</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Some Alchemists know the secrets to making small, handheld bombs that
        can be thrown into combat. When thrown and the word &apos;Bomb!&apos; is
        clearly called, everyone within 5 metres of where the bomb lands takes 1
        damage (that ignores any damage reduction, just like the Blast spell),
        and is knocked back one step.
      </p>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Weapon Safety</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Only LARP-safe weapons and spell packets may be used in combat in
        Ultimate Warlock. Players bringing their own weapons and shields need to
        have them signed off by a Weapons Officer prior to using them in game.
      </p>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Spell Packets</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Small fabric pouches, typically filled with seeds. They should fit
          easily into your hand, and be pliable enough that you can squish them
          with your thumb. Recommended in bright colours so players can clearly
          see them being thrown and easily find them after a battle.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Weapons</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          LARP weapons are typically foam blades around a fibreglass core (or
          similar material), commonly sealed with latex. Manufacturers like
          Calimacil or Epic Armoury make weapons specifically for roleplay.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Firearms</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Guns should fire soft foam bullets, and must be obviously dissimilar
          to a real firearm. Any guns that look like they could pass for real
          firearms will be banned from use in the game. No firearm in Ultimate
          Warlock can be loaded with more than six shots at one time.
        </CardContent>
      </Card>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Combat Safety</h2>
      <p className="text-sm text-muted-foreground mb-3">
        Striking the head or groin is strictly prohibited. If you are repeatedly
        hit in these areas by another player, and they do not desist this
        behaviour when asked, please raise the issue with a Welfare Officer.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        No thrusting or stabbing. This can not only injure your fellow players,
        but also damage the weapons you&apos;re using.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Engaging in combat in a theatrical style with big, clearly telegraphed
        swings and slashes not only looks cooler, but is safer for all involved.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        Remember that people you are fighting are playing the game too! If they
        are in danger of an obstacle behind them, call out &apos;caution&apos;
        or ask for a Time Out using a T-sign, and move your battle to a safer
        location.
      </p>
      <p className="text-sm text-muted-foreground mb-3">
        If you drop to 0 HP and begin Bleeding Out, consider crawling a short
        distance away from the action if you believe you may be a hazard to
        players still engaged in combat around you.
      </p>

      <hr className="my-6 border-border" />

      <h2 className="text-xl font-semibold mt-6 mb-2">Combat Calls</h2>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Medic</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Used for real-life medical attention. If you are injured in game, call
          for a healer instead.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Time Out</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Can be asked for with a T-sign hand symbol to briefly drop out of
          character and ask questions.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Caution</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Call this if you can see a hazard that another player may not be aware
          of. Then, remove the hazard or relocate to avoid it before resuming
          play.
        </CardContent>
      </Card>

      <Card className="mb-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Down</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          If you hear this call, you must immediately pause play and take a knee
          if it is safe to do so. Down is called when someone may be badly hurt,
          or there is a potential emergency. Continuing to play after Down is
          called may result in temporary suspension from the game.
        </CardContent>
      </Card>

      <Badge variant="outline">
        No head or groin strikes. No thrusting. Safety first.
      </Badge>
    </RulebookPage>
  );
}
